import axios, { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { GradientButton } from '../../components/Buttons';
import BodyWrap from '../../components/BodyWrap';
import { Modal, Text, TextInput } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Project } from '../../types/Project';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { clearLocalStorage } from '../../utils/authUtils';

function Dashboard() {
  useEffect(() => {
    document.title = 'Dashboard | PromptSlate';
  }, []);
  const navigate = useNavigate();
  const [createProjectModalOpened, setCreateProjectModalOpened] = useState(false);
  let [projectListMessage, setProjectListMessage] = useState<string>('');
  let [projectCreateMessage, setProjectCreateMessage] = useState<string>('');
  let [isLoading, setIsLoading] = useState<boolean>(false);

  const form = useForm({
    initialValues: {
      name: '',
      key: '',
    },

    validate: {
      name: (value: string) => {
        if (!value) {
          return 'Enter a project name';
        }
      },
      key: (value: string) => {
        if (!value) {
          return 'Enter a key for new project';
        }
        const isValid = /^[a-z]+(_[a-z]+)*$/.test(value);
        if (!isValid) {
          return 'Key should only contain lowercase alphabets and underscores (e.g., chat_bot)';
        }
      },
    },
  });

  const {
    error,
    data,
    refetch: refetchProjects,
  } = useQuery({
    queryKey: ['projectList'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const authToken = localStorage.getItem('authToken');

        if (authToken) {
          const headers = {
            Authorization: 'Bearer ' + authToken,
          };
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/project`, {
            headers: headers,
          });
          const projects: Project[] = response.data.projects;
          return projects;
        } else {
          return [];
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.status === 401) {
            clearLocalStorage();
            navigate('/login');
          }
          if (axiosError.code === 'ERR_BAD_REQUEST') {
            setProjectListMessage('Unable to fetch projects');
            return [];
          } else {
            setProjectListMessage('A network error occurred. Please try again later.');
            return [];
          }
        } else {
          setProjectListMessage('An error occurred. Please try again later.');
          return [];
        }
      }
    },
  });

  const handleSubmit = async (values: any) => {
    setProjectCreateMessage('');
    setIsLoading(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };
      await axios.post(
        `${process.env.REACT_APP_API_URL}/project/create`,
        {
          name: values.name,
          key: values.key,
        },
        {
          headers: headers,
        }
      );
      values.name = '';
      refetchProjects();
      setIsLoading(false);
      setCreateProjectModalOpened(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        }
        if (axiosError.code === 'ERR_BAD_REQUEST') {
          setProjectCreateMessage('Invalid project name');
        } else {
          setProjectCreateMessage('A network error occured. Please try again later.');
        }
      } else {
        setProjectCreateMessage('An error occured. Please try again later');
      }
      setIsLoading(false);
    }
  };

  return (
    <BodyWrap>
      <Modal
        opened={createProjectModalOpened}
        onClose={() => setCreateProjectModalOpened(false)}
        title={<Text size={'xl'}>Create a project</Text>}
        size={'lg'}
        radius="lg"
      >
        <form noValidate onSubmit={form.onSubmit((values) => handleSubmit(values))}>
          <TextInput
            className="mt-5"
            size="md"
            placeholder="Name of new project"
            {...form.getInputProps('name')}
          />
          <div className="mt-5 flex flex-col">
            <span>Choose a key for your project</span>
            <span className="text-sm">
              The key should be in lowercase seperated by underscore (eg. chat_bot)
            </span>
            <TextInput
              className="mt-2"
              size="md"
              placeholder="Enter a key "
              {...form.getInputProps('key')}
            />
          </div>

          <GradientButton className="mt-5 mb-5 w-full" type="submit" disabled={isLoading}>
            {isLoading ? 'Creating project...' : 'Create project'}
          </GradientButton>
        </form>
      </Modal>
      <div className="text-xl pb-5 ">Projects</div>

      {data ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-5">
          {data.map((item) => (
            <NavLink to={`/project/${item.key}`} key={item.id.toString()}>
              <div className="h-[150px] shadow-[3px_3px_0px_0px_rgba(71,_85,_105,_0.08)] hover:shadow-[4px_4px_0px_0px_rgba(0,_0,_0,_0.2)] bg-white transition-shadow duration-100 ease-in-out border rounded-xl p-5 flex items-center justify-center text-md cursor-pointer">
                {item.name}
              </div>
            </NavLink>
          ))}
          <div
            onClick={() => setCreateProjectModalOpened(true)}
            className="h-[150px] shadow-[3px_3px_0px_0px_rgba(71,_85,_105,_0.08)] hover:shadow-[4px_4px_0px_0px_rgba(0,_0,_0,_0.2)] bg-white transition-shadow duration-100 ease-in-out border border-dashed text-slate-600 rounded-xl p-5 flex items-center justify-center text-md cursor-pointer"
          >
            + Create Project
          </div>
        </div>
      ) : (
        <div className="h-full flex flex-col justify-center items-center">
          <div className="flex flex-col justify-center items-center pb-12">
            {projectListMessage ? (
              <div>{projectListMessage}</div>
            ) : (
              <div className="flex flex-col">
                <div
                  onClick={() => setCreateProjectModalOpened(true)}
                  className="h-[150px] shadow-[3px_3px_0px_0px_rgba(71,_85,_105,_0.08)] hover:shadow-[4px_4px_0px_0px_rgba(0,_0,_0,_0.2)] bg-white transition-shadow duration-100 ease-in-out border border-dashed text-slate-600 rounded-xl p-5 flex items-center justify-center text-md cursor-pointer"
                >
                  + Create Project
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </BodyWrap>
  );
}

export default Dashboard;
